import {
  ReorderableElement,
  ReorderableList,
} from '@monorepo/shared/componentsV2/dragAndDrop/ReorderableList';
import { List } from '@monorepo/shared/componentsV2/List';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { lookFor } from '@monorepo/shared/utils/lookFor';
import React, { useMemo } from 'react';
import {
  DisplayQueryColumn,
  isColumnReorderable,
} from '@monorepo/shared/componentsV2/QueryResultsTable/resultsMapper';
import { StyledListContainer, StyledNoResults } from './styles';
import {
  FilteredColumnListItem,
  StyledUnorderableListElement,
  UnorderableColumnListItem,
} from './UnorderableColumnListItem';

export function VisibleColumnList({
  visibleUnorderableColumns,
  visibleOrderableColumns,
  isSaving,
  updateColumnOrder,
  toggleColumnVisibility,
  filter,
}: {
  visibleUnorderableColumns: DisplayQueryColumn[];
  visibleOrderableColumns: (ReorderableElement & DisplayQueryColumn)[];
  isSaving: boolean;
  updateColumnOrder: (orderedElements: string[]) => void;
  toggleColumnVisibility: (column: DisplayQueryColumn) => void;
  filter: string;
}) {
  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();
  const filteredVisibleColumns = useMemo(
    () =>
      [...visibleUnorderableColumns, ...visibleOrderableColumns].filter(
        (column) => lookFor(filter, column, ['columnLabel']),
      ),
    [filter, visibleUnorderableColumns, visibleOrderableColumns],
  );
  return (
    <StyledListContainer>
      <SkipLink linkText="Skip past columns" />
      {filter.trim() === '' ? (
        <>
          <List>
            {visibleUnorderableColumns.map((column) => (
              <UnorderableColumnListItem
                key={column.columnName}
                column={column}
                disableShowHideButton
                toggleColumnVisibility={toggleColumnVisibility}
              />
            ))}
          </List>
          <ReorderableList
            orderedElements={visibleOrderableColumns}
            onOrderChange={updateColumnOrder}
            uniqueDragListId="reorderableColumnNames"
            listAriaLabel="Reorderable Column Names"
            usePortal
            disableDragging={isSaving}
          />
        </>
      ) : (
        <List>
          {filteredVisibleColumns.length > 0 ? (
            filteredVisibleColumns.map((column) => (
              <FilteredColumnListItem
                column={column}
                disableShowHideButton={isSaving || !isColumnReorderable(column)}
                toggleColumnVisibility={toggleColumnVisibility}
              />
            ))
          ) : (
            <StyledUnorderableListElement>
              <StyledNoResults>No results.</StyledNoResults>
            </StyledUnorderableListElement>
          )}
        </List>
      )}
      <SkipLinkTarget />
    </StyledListContainer>
  );
}
